import React, { Component } from 'react';
import { connect } from 'react-redux';
import PlyrWithClosedCaptions from '@lwtears/lwt-common-frontend/lib/PlayerWithClosedCaptions';
import InactivityManager from 'utils/InactivityManager';
import FlippingComponent from 'components/FlippingComponent/FlippingComponent';
import PropTypes from 'prop-types';
import { playMultipleSounds } from 'utils/play-multiple-sounds';
import './FlipCard.scss';
import { captionFormat } from 'utils/caption-util';
import { formatPlyrVolume } from 'utils/plyr-util';
import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const REASON = {
  didNotFlipCard: 'Did not flip card'
};
const CONTAINER_STYLE = {
  width: 'auto',
  height: '100%',
  maxWidth: '360px'
};

const PLYR_STYLE_OVERRIDE = { minWidth: '200px', height: '100%', width: 'auto' };
export class FlipCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      roundEnd: false,
      showVideo: false,
      playVideo: false
    };
    this.vidRef = React.createRef();
    this.inactivityManager = new InactivityManager(
      TIMEOUT,
      this.playInstructions,
      REASON.didNotFlipCard
    );
    this.promise = null;
    this.timers = [];
  }

  playInstructions = () => {
    const {
      props: {
        module,
        currentRound: {
          audio,
          data: {
            instructions: { beforeFlip }
          }
        }
      }
    } = this;
    if (module === 'letters') {
      const snd = audio[beforeFlip[beforeFlip.length - 1].src];
      if (snd) {
        snd.play();
      }
    } else {
      const snd = audio[beforeFlip.src];
      if (snd) {
        snd.play();
      }
    }
  };

  async componentDidMount() {
    const {
      props: {
        module,
        currentRound: {
          audio,
          data: {
            instructions: { beforeFlip }
          }
        }
      }
    } = this;
    this.timers.push(
      setTimeout(() => {
        if (module !== 'letters') {
          this.playInstructions();
        }
        this.inactivityManager.watch();
      }, 500)
    );
    if (module === 'letters') {
      this.promise = playMultipleSounds(500, audio, beforeFlip);
      await this.promise;
    }
  }

  componentWillUnmount() {
    this.inactivityManager.clear();
    this.props.currentRound.stopAllSounds();
    this.timers.forEach(tID => clearTimeout(tID));
    this.promise && this.promise.cancel();
    if (this.vidRef.current) {
      this.vidRef.current.muted = true;
    }
  }

  onFlip = async () => {
    await this.setState({
      showVideo: true,
      playVideo: true
    });
    const {
      props: {
        currentRound: {
          audio,
          data: {
            instructions: { afterFlip }
          },
          stopAllSounds
        },
        onRoundEnd
      }
    } = this;
    stopAllSounds();
    this.timers.forEach(tID => clearTimeout(tID));
    this.inactivityManager.clear();
    if (afterFlip) {
      this.promise = playMultipleSounds(500, audio, afterFlip);
      await this.promise;
      onRoundEnd();
    }

    this.setState({ roundEnd: true });
  };

  onRoundEnd = () => {
    this.setState({ playVideo: false });
    this.props.onRoundEnd();
  };

  render() {
    const {
      props: {
        module,
        currentRound: {
          data: { cardFront, cardBack }
        }
      }
    } = this;

    const {
      accessibility: {
        audioSettings,
        closedCaptionsSettings: { showClosedCaptions, closedCaptionSize },
        videoNarrationSettings: { showSettings }
      }
    } = this.props.lwtStoreState.settings;

    const PlyrProps = {
      src: cardBack.src,
      type: 'video/mp4',
      title: '',
      onDone: this.onRoundEnd,
      options: {
        autoplay: this.state.showVideo && this.state.playVideo && this.state.roundEnd,
        withNativeControls: true,
        locale: getLocale(),
        captions: {
          active: showClosedCaptions,
          styles: { fontSize: closedCaptionSize },
          hideCaptionToggle: true
        },
        plyrStyle: PLYR_STYLE_OVERRIDE,
        disableResize: true,
        containerStyles: CONTAINER_STYLE,
        narrationsAsDefault: showSettings,
        showSettings: showSettings
      },
      config: { volume: formatPlyrVolume(audioSettings.volume, audioSettings.muteSounds) },
      captions: [captionFormat(cardBack.vtt)],
      narration: {
        src: cardBack.narrationSrc ?? '',
        captions: [{ src: cardBack.narrationVtt ?? '', label: '', kind: 'captions' }]
      }
    };

    let faceFront = null;
    const cardDescriptions = {
      cardFrontDescription: cardFront.description,
      cardBackDescription: cardBack.description
    };

    faceFront = <img src={cardFront.src} alt="" width={360} height={540} />;

    //eslint-disable-next-line
    const FaceBack = ({ shouldShowVideo }) => {
      if (module === 'letters') return <img src={cardBack.src} alt="" width={360} height={540} />;
      return (
        <div className={'flip-card-back'}>
          {shouldShowVideo ? <PlyrWithClosedCaptions {...PlyrProps} /> : null}
        </div>
      );
    };

    return (
      <div className={'flip-card'}>
        <FlippingComponent
          data-test="unit-test-data"
          faceFront={faceFront}
          faceBack={<FaceBack shouldShowVideo={this.state.showVideo} />}
          faceBackProps={{ disabled: true }}
          onCardTurned={() => this.onFlip()}
          cardDescriptions={cardDescriptions}
        />
      </div>
    );
  }
}

FlipCard.propTypes = {
  currentRound: PropTypes.object,
  onRoundEnd: PropTypes.func,
  module: PropTypes.string,
  lwtStoreState: PropTypes.object
};
const mapStateToProps = state => ({
  module: state.ui.module
});
export default connect(mapStateToProps)(withLwtStore(FlipCard));
